import React from "react";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";

import SearchIcon from "@material-ui/icons/Search";
import SendIcon from "@material-ui/icons/Send";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import WabaTemplatePreview from "./WabaTemplatePreview";
import { useWabaTemplateModal } from "./useWabaTemplateModal";

const useStyles = makeStyles((theme) => ({
  searchField: { marginBottom: theme.spacing(1) },
  list: { maxHeight: 360, overflowY: "auto", ...theme.scrollbarStyles },
  listItem: { border: "1px solid #eee", borderRadius: 6, marginBottom: 6 },
  langChip: { fontSize: "0.7rem", marginLeft: 8 },
  section: { marginTop: theme.spacing(1), marginBottom: theme.spacing(0.5) },
  sectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  varField: { marginBottom: theme.spacing(1.5) },
  previewLabel: { fontSize: "0.75rem", color: theme.palette.text.secondary, marginBottom: 6 },
  emptyList: { textAlign: "center", padding: theme.spacing(3), color: theme.palette.text.secondary },
  titleRow: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
}));

export default function WabaTemplateModal({ open, onClose, ticketId, whatsappId, onSent }) {
  const classes = useStyles();
  const {
    filtered,
    loading,
    search,
    setSearch,
    selected,
    parsed,
    selectTemplate,
    backToList,
    headerMediaUrl,
    setHeaderMediaUrl,
    headerTextVars,
    setHeaderVar,
    bodyVars,
    setBodyVar,
    complete,
    sending,
    handleSend,
  } = useWabaTemplateModal({ open, ticketId, whatsappId, onSent, onClose });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className={classes.titleRow}>
          {selected && (
            <IconButton size="small" onClick={backToList} title="Volver a la lista">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          {selected ? `Plantilla: ${selected.name}` : "Enviar plantilla de Meta"}
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {/* PASO 1 — Selección de plantilla */}
        {!selected && (
          <>
            <TextField
              className={classes.searchField}
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Buscar plantilla aprobada..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                ),
              }}
            />
            {loading ? (
              <div className={classes.emptyList}><CircularProgress size={28} /></div>
            ) : filtered.length === 0 ? (
              <div className={classes.emptyList}>
                <Typography>
                  No hay plantillas aprobadas para esta conexión. Sincroniza en
                  "Plantillas Meta".
                </Typography>
              </div>
            ) : (
              <List className={classes.list}>
                {filtered.map((t) => (
                  <ListItem
                    button
                    key={t.id}
                    className={classes.listItem}
                    onClick={() => selectTemplate(t)}
                  >
                    <ListItemText
                      primary={
                        <span>
                          {t.name}
                          <Chip size="small" label={t.language} className={classes.langChip} />
                        </span>
                      }
                      secondary={t.category || undefined}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        {/* PASO 2 — Variables + preview fiel */}
        {selected && parsed && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {/* Header media */}
              {parsed.header?.isMedia && (
                <div className={classes.section}>
                  <div className={classes.sectionLabel}>
                    Encabezado ({parsed.header.format})
                  </div>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="URL pública del archivo (https://...)"
                    value={headerMediaUrl}
                    onChange={(e) => setHeaderMediaUrl(e.target.value)}
                  />
                </div>
              )}

              {/* Header texto con variables */}
              {parsed.header && !parsed.header.isMedia && parsed.header.varCount > 0 && (
                <div className={classes.section}>
                  <div className={classes.sectionLabel}>Variables del encabezado</div>
                  {Array.from({ length: parsed.header.varCount }).map((_, i) => (
                    <TextField
                      key={`h-${i}`}
                      className={classes.varField}
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={`Variable {{${parsed.header.varKeys?.[i] ?? i + 1}}}`}
                      value={headerTextVars[i] || ""}
                      onChange={(e) => setHeaderVar(i, e.target.value)}
                    />
                  ))}
                </div>
              )}

              {/* Body variables */}
              {parsed.body?.varCount > 0 ? (
                <div className={classes.section}>
                  <div className={classes.sectionLabel}>Variables del cuerpo</div>
                  {Array.from({ length: parsed.body.varCount }).map((_, i) => (
                    <TextField
                      key={`b-${i}`}
                      className={classes.varField}
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={`Variable {{${parsed.body.varKeys?.[i] ?? i + 1}}}`}
                      value={bodyVars[i] || ""}
                      onChange={(e) => setBodyVar(i, e.target.value)}
                    />
                  ))}
                </div>
              ) : (
                !parsed.header?.isMedia &&
                !(parsed.header && parsed.header.varCount > 0) && (
                  <Typography color="textSecondary" style={{ marginTop: 8 }}>
                    Esta plantilla no tiene variables. Solo confirma el envío.
                  </Typography>
                )
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Divider style={{ marginBottom: 12 }} />
              <div className={classes.previewLabel}>Vista previa (como la verá el cliente)</div>
              <WabaTemplatePreview
                parsed={parsed}
                headerMediaUrl={headerMediaUrl}
                headerTextVars={headerTextVars}
                bodyVars={bodyVars}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancelar</Button>
        {selected && (
          <Button
            variant="contained"
            color="primary"
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            onClick={handleSend}
            disabled={sending || !complete}
          >
            {sending ? "Enviando..." : "Enviar plantilla"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
